import React from "react";
import {
  Paper,
  Stack,
  Typography,
  Box,
  LinearProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Grid,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import ParticipantCoverageFilters, {
  DatePreset,
} from "./ParticipantCoverageFilters";
import { DataCoverageService } from "../../../services/dataCoverageService";
import { apiService } from "../../../config/api";
import { isCommonFitbitHealthKitMetric } from "../../../constants/dataCoverageCommonMetrics";
import type { StudyDeviceAvailability } from "./studyDeviceAvailability";

interface StudyLevelMetricCoverageCardProps {
  studyId: string;
  deviceAvailability: StudyDeviceAvailability;
  onDataChange?: (data: StudyMetricCoverageResult | null) => void;
}

const MAX_VISIBLE_ROWS = 20;
const ROW_HEIGHT = 37; // pixels per row
const TABLE_MAX_HEIGHT = `${MAX_VISIBLE_ROWS * ROW_HEIGHT}px`;

interface AggregatedMetricCoverage {
  metricKey: string;
  metricLabel: string;
  participantCount: number;
  avgCoveragePct: number;
  minCoveragePct: number;
  maxCoveragePct: number;
  participantsWithData: number;
}

interface StudyMetricCoverageResult {
  study: {
    studyId: string;
    studyName: string;
    totalParticipants: number;
  };
  selectedDeviceType: "fitbit" | "healthkit" | "both";
  range: {
    fromDate: string;
    endDate: string;
    totalDays: number;
    preset: string | null;
  };
  summary: {
    participantsAnalyzed: number;
    avgCoveragePct: number;
  };
  metrics: AggregatedMetricCoverage[];
  fetch?: {
    source: string;
    statusMessage: string;
    refreshedFromApi: boolean;
    participantsWithMissingFitbitData: number;
    participantsRefreshedFromApi: number;
    participantsWithFailedFitbitRefresh: number;
    failedFitbitRefreshes: {
      participantId: string;
      participantName: string;
      ranges: { startDate: string; endDate: string; dayCount: number }[];
    }[];
  };
}

const StudyLevelMetricCoverageCard: React.FC<
  StudyLevelMetricCoverageCardProps
> = ({ studyId, deviceAvailability, onDataChange }) => {
  const [fromDate, setFromDate] = React.useState("");
  const [endDate, setEndDate] = React.useState("");
  const [preset, setPreset] = React.useState<DatePreset | null>("7d");
  const [deviceType, setDeviceType] = React.useState<
    "fitbit" | "healthkit" | "both"
  >("both");
  const [running, setRunning] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [result, setResult] = React.useState<StudyMetricCoverageResult | null>(
    null,
  );
  const [progressStatus, setProgressStatus] = React.useState("");

  const hasFitbitParticipants = deviceAvailability.fitbitParticipants > 0;
  const hasHealthkitParticipants = deviceAvailability.healthkitParticipants > 0;
  const canSelectBoth = hasFitbitParticipants && hasHealthkitParticipants;

  React.useEffect(() => {
    if (deviceType === "fitbit" && !hasFitbitParticipants) {
      if (hasHealthkitParticipants) {
        setDeviceType("healthkit");
      } else {
        setDeviceType("both");
      }
      return;
    }

    if (deviceType === "healthkit" && !hasHealthkitParticipants) {
      if (hasFitbitParticipants) {
        setDeviceType("fitbit");
      } else {
        setDeviceType("both");
      }
      return;
    }

    if (deviceType === "both" && !canSelectBoth) {
      if (hasFitbitParticipants) {
        setDeviceType("fitbit");
      } else if (hasHealthkitParticipants) {
        setDeviceType("healthkit");
      }
    }
  }, [
    canSelectBoth,
    deviceType,
    hasFitbitParticipants,
    hasHealthkitParticipants,
  ]);

  // Initialize dates on mount
  React.useEffect(() => {
    const today = new Date();
    const end = today.toISOString().slice(0, 10);
    const start = new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 10);
    setFromDate(start);
    setEndDate(end);
  }, []);

  // Notify parent when data changes
  React.useEffect(() => {
    if (onDataChange) {
      onDataChange(result);
    }
  }, [result, onDataChange]);

  const applyPreset = (nextPreset: DatePreset | null) => {
    setPreset(nextPreset);
    if (!nextPreset) return;

    const today = new Date();
    const end = today.toISOString().slice(0, 10);
    let start = new Date(today);

    if (nextPreset === "7d") {
      start = new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000);
    } else if (nextPreset === "1m") {
      start = new Date(today);
      start.setMonth(start.getMonth() - 1);
      start.setDate(start.getDate() + 1);
    } else if (nextPreset === "6m") {
      start = new Date(today);
      start.setMonth(start.getMonth() - 6);
      start.setDate(start.getDate() + 1);
    } else {
      start = new Date(today);
      start.setFullYear(start.getFullYear() - 1);
      start.setDate(start.getDate() + 1);
    }

    setFromDate(start.toISOString().slice(0, 10));
    setEndDate(end);
  };

  const handleRun = async () => {
    if (!fromDate || !endDate) {
      setError("Please select both from and end dates.");
      return;
    }

    try {
      setRunning(true);
      setError(null);
      setProgressStatus("Fetching study data...");
      setResult(null);

      const token = await apiService.getAuthToken();
      if (!token) {
        setError("Sign in to run coverage analysis.");
        return;
      }

      const response = await DataCoverageService.getStudyMetricCoverage(
        studyId,
        {
          fromDate,
          endDate,
          preset,
          deviceType,
          refreshIfMissing: true,
        },
        token,
      );

      if (response.success && (response as any).data) {
        setResult((response as any).data);
        setProgressStatus("");
      } else {
        setError("Failed to compute study-level metric coverage.");
      }
    } catch (err: any) {
      setError(err?.message || "Error computing metric coverage.");
    } finally {
      setRunning(false);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Stack spacing={2.5}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Study Coverage Summary
        </Typography>

        {/* Controls */}
        <Stack spacing={2}>
          {/* Device Type Selector */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1 }}>
              Device Type
            </Typography>
            <ToggleButtonGroup
              value={deviceType}
              exclusive
              onChange={(_, value) => {
                if (value) setDeviceType(value);
              }}
              size="small"
            >
              <ToggleButton value="fitbit" disabled={!hasFitbitParticipants}>
                Fitbit Only
              </ToggleButton>
              <ToggleButton
                value="healthkit"
                disabled={!hasHealthkitParticipants}
              >
                HealthKit Only
              </ToggleButton>
              <ToggleButton value="both" disabled={!canSelectBoth}>
                Both / Common Metrics
              </ToggleButton>
            </ToggleButtonGroup>

            {!hasFitbitParticipants && (
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ mt: 0.5 }}
              >
                No participant with Fitbit is enrolled in this study.
              </Typography>
            )}
            {!hasHealthkitParticipants && (
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ mt: 0.25 }}
              >
                No participant with HealthKit is enrolled in this study.
              </Typography>
            )}
          </Box>

          {/* Date Filters */}
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <ParticipantCoverageFilters
              disabled={false}
              fromDate={fromDate}
              endDate={endDate}
              preset={preset}
              onFromDateChange={(value) => {
                setPreset(null);
                setFromDate(value);
              }}
              onEndDateChange={(value) => {
                setPreset(null);
                setEndDate(value);
              }}
              onPresetChange={applyPreset}
              onRun={handleRun}
              running={running}
            />
          </Box>
        </Stack>

        {/* Loading Progress */}
        {running && (
          <Stack spacing={1}>
            <LinearProgress variant="indeterminate" />
            <Typography variant="body2" color="textSecondary">
              {progressStatus || "Running..."}
            </Typography>
          </Stack>
        )}

        {/* Error */}
        {error && <Alert severity="error">{error}</Alert>}

        {result?.fetch?.statusMessage && (
          <Alert severity="info">{result.fetch.statusMessage}</Alert>
        )}

        {result?.fetch?.failedFitbitRefreshes &&
          result.fetch.failedFitbitRefreshes.length > 0 && (
            <Alert severity="warning">
              Fitbit data could not be retrieved for:{" "}
              {result.fetch.failedFitbitRefreshes
                .map((entry) => {
                  const ranges = entry.ranges
                    .map((range) => `${range.startDate} to ${range.endDate}`)
                    .join(", ");
                  return `${entry.participantName} (${ranges})`;
                })
                .join("; ")}
            </Alert>
          )}

        {/* Results */}
        {result && !running && (
          <Stack spacing={2}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Results ({result.range.fromDate} to {result.range.endDate})
            </Typography>

            {/* Summary Stats Row */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    textAlign: "center",
                    backgroundColor: "#f5f5f5",
                  }}
                >
                  <Typography variant="caption" color="textSecondary">
                    Participants Analyzed
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                    {result.summary.participantsAnalyzed} /{" "}
                    {result.study.totalParticipants}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    textAlign: "center",
                    backgroundColor: "#e3f2fd",
                  }}
                >
                  <Typography variant="caption" color="textSecondary">
                    Avg Coverage
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                    {result.summary.avgCoveragePct.toFixed(1)}%
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    textAlign: "center",
                    backgroundColor: "#f5f5f5",
                  }}
                >
                  <Typography variant="caption" color="textSecondary">
                    Device Type
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                    {result.selectedDeviceType === "both"
                      ? "Both"
                      : result.selectedDeviceType.charAt(0).toUpperCase() +
                        result.selectedDeviceType.slice(1)}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* Metrics Table */}
            {result.metrics.length > 0 ? (
              <>
                {/* Show filtered metrics note when "Both" is selected */}
                {deviceType === "both" && (
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ mb: 1 }}
                  >
                    Note: Showing only metrics available in both Fitbit and
                    HealthKit
                  </Typography>
                )}
                <TableContainer
                  component={Paper}
                  sx={{ maxHeight: TABLE_MAX_HEIGHT, overflowY: "auto" }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Metric</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="center">
                          Participants
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="center">
                          Avg Coverage
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="center">
                          Min Coverage
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="center">
                          Max Coverage
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {result.metrics
                        .filter((metric) =>
                          deviceType === "both"
                            ? isCommonFitbitHealthKitMetric(metric.metricKey)
                            : true,
                        )
                        .map((metric) => (
                          <TableRow key={metric.metricKey}>
                            <TableCell>
                              <Typography variant="body2">
                                {metric.metricLabel}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body2">
                                {metric.participantsWithData}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 600 }}
                              >
                                {metric.avgCoveragePct.toFixed(1)}%
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body2">
                                {metric.minCoveragePct.toFixed(1)}%
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body2">
                                {metric.maxCoveragePct.toFixed(1)}%
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            ) : (
              <Alert severity="info">
                No metrics available for the selected device type and date
                range.
              </Alert>
            )}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};

export default StudyLevelMetricCoverageCard;
